import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { LeadFilterBar, LeadWidgets, applyExtraFilters, EMPTY_EXTRA } from '../components/leads/LeadInsights';
import { LeadQuickAction } from '../components/leads/LeadQuickAction';
import { LeadTimeline } from '../components/leads/LeadTimeline';
import { AgentDailyView } from '../components/leads/AgentDailyView';
import { LeadCSVImport } from '../components/leads/LeadCSVImport';
import { LeadReports } from '../components/leads/LeadReports';
import { LeadTargetWidget } from '../components/leads/LeadTargetWidget';
import { LeadCustomFields } from '../components/leads/LeadCustomFields';
import { LeadCustomFieldInputs } from '../components/leads/LeadCustomFieldInputs';
import { getCustomFields, getCustomValues, setCustomValues } from '../components/leads/leadFields';
import { LeadAppointments } from '../components/leads/LeadAppointments';
import { LeadSettings } from '../components/leads/LeadSettings';
import { LeadIntegrations } from '../components/leads/LeadIntegrations';
import { LeadNotificationBell } from '../components/leads/LeadNotifications';
import { LeadMultiChannel } from '../components/leads/LeadMultiChannel';
import '../components/leads/leadTheme.css';

// ---- static config (matches the approved mock) -----------------
const STATUS_META = {
  not_attempted: { label: 'Not Attempted', badge: '#F5F5F5', color: '#757575', pill: '🔘' },
  new:       { label: 'New',       badge: '#E6F1FB', color: '#0C447C', pill: '🆕' },
  hot:       { label: 'Hot',       badge: '#FCEBEB', color: '#A32D2D', pill: '🔥' },
  warm:      { label: 'Warm',      badge: '#FAEEDA', color: '#633806', pill: '☀️' },
  cold:      { label: 'Cold',      badge: '#EAF3DE', color: '#27500A', pill: '❄️' },
  converted: { label: 'Converted', badge: '#E1F5EE', color: '#085041', pill: '🏆' },
  lost:      { label: 'Lost',      badge: '#F1EFE8', color: '#5F5E5A', pill: '⚪' },
  followup:  { label: 'Followup',  badge: '#FAEEDA', color: '#854F0B', pill: '⏰' },
};
const STATUS_ORDER = ['not_attempted', 'new', 'hot', 'warm', 'cold', 'converted', 'lost'];

// Lead score: 1-10 based on status + activity
function calcLeadScore(l) {
  let score = 5;
  if (l.status === 'hot') score = 9;
  else if (l.status === 'warm') score = 7;
  else if (l.status === 'new') score = 6;
  else if (l.status === 'not_attempted') score = 4;
  else if (l.status === 'cold') score = 3;
  else if (l.status === 'converted') score = 10;
  else if (l.status === 'lost') score = 1;
  // boost if recent reply
  if (l.last_customer_reply_at) {
    const hrs = (Date.now() - new Date(l.last_customer_reply_at)) / 3600000;
    if (hrs < 2) score = Math.min(10, score + 2);
    else if (hrs < 24) score = Math.min(10, score + 1);
  }
  // penalty if no activity
  if (l.last_agent_reply_at) {
    const days = (Date.now() - new Date(l.last_agent_reply_at)) / 86400000;
    if (days > 7) score = Math.max(1, score - 2);
    else if (days > 3) score = Math.max(1, score - 1);
  }
  return score;
}

// Rotting: no activity for 3+ days
function isRotting(l) {
  if (['converted','lost'].includes(l.status)) return false;
  const ref = l.last_agent_reply_at || l.updated_at || l.created_at;
  if (!ref) return false;
  return (Date.now() - new Date(ref)) / 86400000 > 3;
}

const SOURCE_META = {
  whatsapp: { bg: '#E3F2FD', color: '#1565c0', label: '💬 Get Quote' },
  website:  { bg: '#F3E5F5', color: '#6a1b9a', label: '🌐 Website Order' },
  agent:    { bg: '#E8F5E9', color: '#2e7d32', label: '👤 Agent Order' },
  ads:      { bg: '#E3F2FD', color: '#1565c0', label: '📢 Meta Ad' },
};

const PRODUCT_META = {
  'Cotton Saree': { bg: '#E8F5E9', color: '#2e7d32' },
  'Kanjivaram':   { bg: '#FCE4EC', color: '#c62828' },
  'Bridal':       { bg: '#F3E5F5', color: '#6a1b9a' },
};

const userData = JSON.parse(localStorage.getItem('vflow_user') || '{}');
const bizData = JSON.parse(localStorage.getItem('vflow_business') || '{}');
const bizId = bizData?.id || userData?.business_id || userData?.businesses?.[0]?.id;
const currentAgentId = userData?.agent_id || null;

// ---- shared inline style tokens (kept consistent with the rest of the app) ----
const s = {
  page: { padding: 0, fontFamily: 'inherit' },
  tabs: { display: 'flex', gap: 0, background: '#fff', borderBottom: '1px solid #eceef1', padding: '0 20px', position: 'sticky', top: 0, zIndex: 20, overflowX: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  tab: (active) => ({ padding: '14px 18px', fontSize: 13, fontWeight: active ? 700 : 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'color .15s, border-color .15s',
    color: active ? '#25d366' : '#6b7280', borderBottom: active ? '3px solid #25d366' : '3px solid transparent' }),
  header: { background: 'linear-gradient(135deg,#075e54,#0d8a7a,#128c7e)', padding: '22px 26px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 12px rgba(7,94,84,0.25)' },
  headerTitle: { fontSize: 19, fontWeight: 800, color: '#fff', letterSpacing: '-0.01em' },
  headerSub: { fontSize: 12.5, color: 'rgba(255,255,255,0.75)', marginTop: 4, fontWeight: 500 },
  btn: (bg, color) => ({ padding: '9px 18px', background: bg, color, border: 'none', borderRadius: 24, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'opacity .15s', boxShadow: bg === '#fff' ? '0 1px 4px rgba(0,0,0,0.12)' : 'none' }),
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, background: 'transparent', padding: '16px 20px' },
  statCard: (color) => ({ background: '#fff', padding: '16px 18px', borderRadius: 12, borderLeft: `4px solid ${color || '#25d366'}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }),
  statNum: (color) => ({ fontSize: 22, fontWeight: 800, color }),
  statLbl: { fontSize: 11, color: '#8a8a8a', marginTop: 4, fontWeight: 500 },
  filterBar: { background: '#fff', padding: '14px 18px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderRadius: 12, margin: '0 20px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  chip: (active, tone) => ({ padding: '6px 14px', borderRadius: 20, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', transition: 'all .15s',
    border: `1.5px solid ${active ? (tone || '#25d366') : '#e8e8e8'}`, color: active ? (tone || '#25d366') : '#666',
    background: active ? `${tone || '#25d366'}15` : '#fafafa', boxShadow: active ? `0 1px 4px ${tone || '#25d366'}22` : 'none' }),
  search: { marginLeft: 'auto', padding: '8px 14px', border: '1.5px solid #eee', borderRadius: 20, fontSize: 12.5, outline: 'none', background: '#fafafa' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' },
  td: { padding: '10px 14px', fontSize: 13, color: '#444', borderBottom: '1px solid #f8f8f8', whiteSpace: 'nowrap' },
  badge: (bg, color) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: bg, color }),
  slaBarWrap: { width: 80, height: 5, background: '#eee', borderRadius: 3, overflow: 'hidden' },
  slaBarFill: (pct, color) => ({ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }),
  actionBtn: (bg) => ({ width: 28, height: 28, border: 'none', borderRadius: 8, background: bg, cursor: 'pointer', marginRight: 4 }),
  modalInput: { width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10, outline: 'none' },
};

function slaColor(hoursLeft, slaHours) {
  if (hoursLeft == null) return '#ccc';
  if (hoursLeft <= 0) return '#f44336';
  const pct = hoursLeft / slaHours;
  if (pct <= 0.15) return '#f44336';
  if (pct <= 0.5) return '#ff9800';
  return '#25d366';
}

export default function Leads() {
  const [tab, setTab] = useState('admin');
  const [leads, setLeads] = useState([]);

  const [agents, setAgents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [extra, setExtra] = useState(EMPTY_EXTRA);
  const [sortBy, setSortBy] = useState('smart');

  const fetchLeads = useCallback(async () => {
    try {
      const r = await api.get('/leads?business_id=' + bizId);
      setLeads(Array.isArray(r.data) ? r.data : []);
    } catch (e) { console.log(e); }
  }, []);

  const fetchAgentLeads = useCallback(async () => {
    if (!currentAgentId) return;
    try {
      const r = await api.get('/leads/agent/' + currentAgentId);
      setLeads(Array.isArray(r.data) ? r.data : []);
    } catch (e) { console.log(e); }
  }, []);

  const fetchAgents = useCallback(async () => {
    try {
      const r = await api.get('/agents?business_id=' + bizId);
      setAgents(r.data?.agents || []);
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetchAgents();
    if (tab === 'agent') fetchAgentLeads(); else if (tab === 'admin') fetchLeads();
  }, [tab, fetchLeads, fetchAgentLeads, fetchAgents]);

  const updateStatus = async (id, status) => {
    await api.put('/leads/' + id, { status });
    tab === 'agent' ? fetchAgentLeads() : fetchLeads();
  };

  const reassign = async (id) => {
    await api.post(`/leads/${id}/reassign`, { reason: 'manual' });
    fetchLeads();
  };

  const [quickLead, setQuickLead] = useState(null);
  const [timelineLead, setTimelineLead] = useState(null);
  const [showCSV, setShowCSV] = useState(false);
  const [activeReport, setActiveReport] = useState(false);
  const [multiChannelLead, setMultiChannelLead] = useState(null);

  const quickSave = async (id, form) => {
    await api.put('/leads/' + id, { status: form.status, follow_up_date: form.follow_up_date || null, notes: form.comment || undefined });
    fetchLeads();
  };

  const STATUS_RANK = { not_attempted:1, new:2, hot:3, warm:4, followup:5, cold:6, converted:7, lost:8 };
  const sortedLeads = [...leads].sort((a, b) => {
    if (sortBy === 'smart') {
      const rankDiff = (STATUS_RANK[a.status]||9) - (STATUS_RANK[b.status]||9);
      if (rankDiff !== 0) return rankDiff;
      return new Date(b.updated_at) - new Date(a.updated_at);
    }
    if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === 'updated') return new Date(b.updated_at) - new Date(a.updated_at);
    if (sortBy === 'hot') return (STATUS_RANK[a.status]||9) - (STATUS_RANK[b.status]||9);
    return 0;
  });
  const filteredLeads = applyExtraFilters(sortedLeads, extra).filter(l => {
    if (filter === 'all') { /* no-op */ }
    else if (filter === 'sla_warning' && l.sla_display !== 'warning') return false;
    else if (filter === 'sla_exceeded' && l.sla_display !== 'exceeded') return false;
    else if (filter.startsWith('agent_') && String(l.assigned_agent_id) !== filter.replace('agent_', '')) return false;
    else if (filter === 'followup') { if (['converted','lost'].includes(l.status)) return false; if (!l.last_agent_reply_at) return false; if (l.last_customer_reply_at && new Date(l.last_customer_reply_at) >= new Date(l.last_agent_reply_at)) return false; }
    else if (!['sla_warning', 'sla_exceeded', 'followup'].includes(filter) && !filter.startsWith('agent_') && l.status !== filter) return false;
    if (search && !`${l.name} ${l.phone}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: leads.length,
    today: leads.filter(l => new Date(l.created_at).toDateString() === new Date().toDateString()).length,
    slaWarning: leads.filter(l => l.sla_display === 'warning').length,
    reassigned: leads.filter(l => (l.assign_attempt || 1) > 1).length,
    converted: leads.filter(l => l.status === 'converted').length,
    missedFollowup: leads.filter(l => l.follow_up_date && !['converted','lost'].includes(l.status) && new Date(l.follow_up_date) < new Date(new Date().toDateString())).length,
    reEngaged: leads.filter(l => l.status === 'cold' && l.last_customer_reply_at && (!l.last_agent_reply_at || new Date(l.last_customer_reply_at) > new Date(l.last_agent_reply_at))).length,
  };

  return (
    <div className="lead-theme" style={s.page}>
      <div style={s.tabs}>
        <div style={s.tab(tab === 'admin')} onClick={() => setTab('admin')}>📊 Admin Lead Dashboard</div>
        <div style={s.tab(tab === 'daily')} onClick={() => setTab('daily')}>📋 Daily View</div>
        <div style={s.tab(tab === 'agent')} onClick={() => setTab('agent')}>👤 Agent Dashboard</div>
        <div style={s.tab(tab === 'reports')} onClick={() => setTab('reports')}>📈 Reports</div>
        <div style={s.tab(tab === 'appointments')} onClick={() => setTab('appointments')}>📅 Appointments</div>
        <div style={s.tab(tab === 'settings')} onClick={() => setTab('settings')}>⚙️ Settings</div>
        <div style={s.tab(tab === 'integrations')} onClick={() => setTab('integrations')}>🔗 Integrations</div>
        <div style={s.tab(tab === 'waforms')} onClick={() => setTab('waforms')}>🔧 WA Forms</div>
      </div>

      {tab === 'admin' && (
        <AdminDashboard
          leads={filteredLeads} allLeads={leads} extra={extra} setExtra={setExtra} stats={stats} agents={agents}
          filter={filter} setFilter={setFilter} search={search} setSearch={setSearch}
          updateStatus={updateStatus} reassign={reassign} refresh={fetchLeads} quickSave={quickSave} sortBy={sortBy} setSortBy={setSortBy} quickLead={quickLead} setQuickLead={setQuickLead} timelineLead={timelineLead} setTimelineLead={setTimelineLead} showCSV={showCSV} setShowCSV={setShowCSV} fetchLeads={fetchLeads} multiChannelLead={multiChannelLead} setMultiChannelLead={setMultiChannelLead}
        />
      )}
      {tab === 'agent' && (
        <>
          <AgentDashboard leads={leads} updateStatus={updateStatus} />
          <div style={{ padding: '0 20px 20px' }}><LeadTargetWidget leads={leads} agentId={currentAgentId} /></div>
        </>
      )}
      {tab === 'daily' && (
        <AgentDailyView leads={leads} onOpenTimeline={(l) => setTimelineLead(l)} />
      )}
      {tab === 'reports' && (
        <LeadReports leads={leads} agents={agents} />
      )}
      {tab === 'appointments' && <LeadAppointments leads={leads} />}
      {tab === 'settings' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
          <div>
            <div style={{ padding: '16px 20px 0' }}>
              <LeadTargetWidget leads={leads} />
            </div>
            <LeadCustomFields />
          </div>
          <LeadSettings agents={agents} />
        </div>
      )}
      {tab === 'integrations' && <LeadIntegrations />}
      {tab === 'waforms' && <WaFormsConfig />}
    </div>
  );
}

// =================================================================
// ADMIN DASHBOARD
// =================================================================
function AdminDashboard({ leads, allLeads, extra, setExtra, stats, agents, filter, setFilter, search, setSearch, updateStatus, reassign, refresh, quickSave, sortBy, setSortBy, quickLead, setQuickLead, timelineLead, setTimelineLead, showCSV, setShowCSV, fetchLeads, multiChannelLead, setMultiChannelLead }) {
  const [showModal, setShowModal] = useState(false);
  const exportLeadsCSV = () => {
    if (!leads.length) { alert('No leads to export!'); return; }
    const headers = ['Name', 'Phone', 'Source', 'Product', 'Button Clicked', 'Status', 'Assigned Agent', 'Created At'];
    const rows = leads.map(l => [
      l.name || '',
      l.phone || '',
      l.keyword || l.source || '',
      l.product_tag || '',
      l.button_clicked || '',
      l.status || '',
      l.agent_name || '',
      l.created_at ? new Date(l.created_at).toLocaleString() : ''
    ]);
    const escapeCSV = (val) => {
      const str = String(val).replace(/"/g, '""');
      return /[",\n]/.test(str) ? `"${str}"` : str;
    };
    const csvContent = [headers, ...rows].map(row => row.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `leads_export_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const [editLead, setEditLead] = useState(null);
  const [customVals, setCustomVals] = useState({});
  const [leadType, setLeadType] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', source: 'manual', status: 'new', product_tag: '', keyword: '', notes: '', assigned_agent_id: '' });

  const openNew = () => { setCustomVals({}); setLeadType(''); setEditLead(null); setForm({ name: '', phone: '', source: 'manual', status: 'new', product_tag: '', keyword: '', notes: '', assigned_agent_id: '' }); setShowModal(true); };
  const openEdit = (l) => { const cv = getCustomValues(l.id); setCustomVals(cv); setLeadType(cv.__lead_type || ''); setEditLead(l); setForm({ ...l, assigned_agent_id: l.assigned_agent_id || '' }); setShowModal(true); };

  const saveLead = async () => {
    const missing = getCustomFields().find(f => f.required && f.type !== 'checkbox' && !String(customVals[f.id] ?? '').trim());
    if (missing) { alert(missing.label + ' required'); return; }
    let savedId = editLead ? editLead.id : null;
    try {
      if (editLead) {
        await api.put('/leads/' + editLead.id, { status: form.status, assigned_agent_id: form.assigned_agent_id || null, notes: form.notes, product_tag: form.product_tag });
      } else {
        const created = await api.post('/leads', { business_id: bizId, name: form.name, phone: form.phone, source: form.source, status: form.status, product_tag: form.product_tag, keyword: form.keyword, notes: form.notes });
        savedId = created && created.data ? created.data.id : null;
      }
      if (savedId) setCustomValues(savedId, { ...customVals, __lead_type: leadType });
      setShowModal(false);
      refresh();
    } catch (e) { alert('Error: ' + e.message); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await api.delete('/leads/' + id);
    refresh();
  };

  return (
    <div>
      <div style={s.header}>
        <div>
          <div style={s.headerTitle}>📊 Lead Dashboard</div>
          <div style={s.headerSub}>Today · {stats.today} new leads · All Agents</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <LeadNotificationBell leads={leads} />
          <button style={s.btn('transparent', '#fff')} onClick={exportLeadsCSV}>⬇ Export Excel</button>
          <button style={s.btn('rgba(255,255,255,0.2)', '#fff')} onClick={() => setShowCSV(true)}>📥 Import CSV</button>
          <button style={s.btn('#fff', '#075e54')} onClick={openNew}>+ Manual Lead</button>
        </div>
      </div>

      <div style={s.statsGrid}>
        <div style={s.statCard('#1a1a2e')}><div style={s.statNum('#1a1a2e')}>{stats.total}</div><div style={s.statLbl}>Total Leads</div></div>
        <div style={s.statCard('#25d366')}><div style={s.statNum('#25d366')}>{stats.today}</div><div style={s.statLbl}>New Today</div></div>
        <div style={s.statCard('#ff9800')}><div style={s.statNum('#ff9800')}>{stats.slaWarning}</div><div style={s.statLbl}>SLA Warning</div></div>
        <div style={s.statCard('#f44336')}><div style={s.statNum('#f44336')}>{stats.reassigned}</div><div style={s.statLbl}>Auto Reassigned</div></div>
        <div style={s.statCard('#2196f3')}><div style={s.statNum('#2196f3')}>{stats.converted}</div><div style={s.statLbl}>Converted</div></div>
        <div style={s.statCard('#e53935')}><div style={s.statNum('#e53935')}>{stats.missedFollowup}</div><div style={s.statLbl}>Missed Follow-ups</div></div>
        <div style={s.statCard('#9c27b0')}><div style={s.statNum('#9c27b0')}>{stats.reEngaged}</div><div style={s.statLbl}>Re-engaged Leads</div></div>
      </div>
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, margin: '0 20px 20px', border: '1px solid #eee' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 18 }}>📊 Lead Status Funnel</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', height: 100, background: '#fafbfc', borderRadius: 10, padding: '14px 10px 0' }}>
          {STATUS_ORDER.map(k => {
            const count = leads.filter(l => l.status === k).length;
            const maxCount = Math.max(1, ...STATUS_ORDER.map(s2 => leads.filter(l => l.status === s2).length));
            const heightPct = Math.max(8, (count / maxCount) * 100);
            const color = STATUS_META[k]?.color || '#999';
            return (
              <div key={k} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6, color: '#1a1a2e' }}>{count}</div>
                <div style={{ width: '100%', height: `${heightPct}%`, background: `linear-gradient(180deg, ${color}, ${color}dd)`, borderRadius: '8px 8px 2px 2px', minHeight: 8, boxShadow: `0 2px 6px ${color}33` }} />
                <div style={{ fontSize: 10.5, color: '#888', marginTop: 8, textAlign: 'center', fontWeight: 500 }}>{STATUS_META[k]?.pill || ''} {STATUS_META[k]?.label || k}</div>
              </div>
            );
          })}
        </div>
      </div>

      <LeadWidgets leads={allLeads} agents={agents} extra={extra} setExtra={setExtra} />
<LeadFilterBar leads={allLeads} agents={agents} extra={extra} setExtra={setExtra} />
<div style={s.filterBar}>
        <span style={{ fontSize: 11, color: '#aaa', fontWeight: 600 }}>Filter:</span>
        {['all', 'new', 'hot', 'warm', 'cold', 'followup', 'converted', 'lost'].map(f => (
          <div key={f} style={s.chip(filter === f, f === 'hot' ? '#A32D2D' : f === 'warm' ? '#633806' : f === 'cold' ? '#27500A' : undefined)} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : `${STATUS_META[f]?.pill || ''} ${STATUS_META[f]?.label || f}`}
          </div>
        ))}
        <span style={{ fontSize: 11, color: '#aaa', fontWeight: 600, marginLeft: 6 }}>SLA:</span>
        <div style={s.chip(filter === 'sla_warning', '#ff9800')} onClick={() => setFilter('sla_warning')}>⚠ Warning</div>
        <div style={s.chip(filter === 'sla_exceeded', '#f44336')} onClick={() => setFilter('sla_exceeded')}>🔴 Exceeded</div>
        {agents.map(a => (
          <div key={a.id} style={s.chip(filter === 'agent_' + a.id, '#6d28d9')} onClick={() => setFilter('agent_' + a.id)}>
            {a.name}
          </div>
        ))}
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ padding: '6px 12px', border: '1.5px solid #eee', borderRadius: 20, fontSize: 12, outline: 'none', background: '#fff', fontFamily: 'inherit', color: '#555', fontWeight: 600 }}>
          <option value="smart">⚡ Smart Sort</option>
          <option value="hot">🔥 Hot First</option>
          <option value="updated">🕐 Last Updated</option>
          <option value="newest">🆕 Newest First</option>
        </select>
        <input style={s.search} placeholder="Search name, phone..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <style>{`
        .lead-row { transition: background .12s; }
        .lead-row:nth-child(even) { background: #fafbfc; }
        .lead-row:hover { background: #eef9f3 !important; }
      `}</style>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>#</th>
              <th style={s.th}>Customer</th>
              <th style={s.th}>Source / Button</th>
              <th style={s.th}>Product / Ad</th>
              <th style={s.th}>Assigned Agent</th>
              <th style={s.th}>SLA</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr><td colSpan={8} style={{ padding: '40px 14px', textAlign: 'center', color: '#bbb', fontSize: 13 }}>
                📭 No leads match this filter. Try a different filter or add a new lead.
              </td></tr>
            )}
            {leads.map(l => {
              const src = SOURCE_META[l.source] || SOURCE_META.whatsapp;
              const prod = PRODUCT_META[l.product_tag];
              const color = slaColor(l.hours_left, 15);
              const pct = l.hours_left != null ? Math.max(4, 100 - (l.hours_left / (15)) * 100) : 100;
              return (
                <tr key={l.id} className="lead-row">
                  <td style={{ ...s.td, color: '#aaa', fontSize: 12 }}>{l.id}</td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 700, color: '#1a1a2e', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setTimelineLead(l)}>{l.name}</div>
                      {(() => { const sc = calcLeadScore(l); const c = sc>=8?'#25d366':sc>=6?'#ff9800':sc>=4?'#1565c0':'#aaa'; return <span style={{ fontSize: 9, fontWeight: 800, background: c+'18', color: c, padding: '1px 5px', borderRadius: 6, border: `1px solid ${c}33` }}>{sc}</span>; })()}
                      {isRotting(l) && <span style={{ fontSize: 9, fontWeight: 700, background: '#fff3e0', color: '#e65100', padding: '1px 5px', borderRadius: 6 }}>🔥 Rotting</span>}
                    </div>
                    <div style={{ fontSize: 11, color: '#aaa' }}>📱 {l.phone}</div>
                    {l.last_agent_reply_at && (!l.last_customer_reply_at || new Date(l.last_customer_reply_at) < new Date(l.last_agent_reply_at)) && (
                      <div style={{ fontSize: 10, color: '#854F0B', marginTop: 2 }}>⏰ {Math.floor((Date.now() - new Date(l.last_agent_reply_at).getTime())/86400000)}d waiting</div>
                    )}
                  </td>
                  <td style={s.td}><span style={s.badge(src.bg, src.color)}>{l.keyword ? `💬 ${l.keyword}` : src.label}</span></td>
                  <td style={s.td}>
                    {l.product_tag
                      ? <span style={s.badge(prod?.bg || '#f5f5f5', prod?.color || '#999')}>{l.product_tag}</span>
                      : <span style={s.badge('#f5f5f5', '#999')}>— Manual</span>}
                    {l.source_name && <div style={{ fontSize: 9, color: '#aaa', marginTop: 2 }}>📢 {l.source_name}</div>}
                  </td>
                  <td style={s.td}>{l.agent_name || '—'}</td>
                  <td style={s.td}>
                    {l.status === 'converted' ? (
                      <span style={{ fontSize: 11, color: '#aaa' }}>Converted</span>
                    ) : l.hours_left == null ? (
                      <span style={{ fontSize: 11, color: '#bbb' }}>— No SLA</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color }}>
                          {l.hours_left <= 0 ? '🔴 Exceeded' : `${l.hours_left} hrs left`}
                        </span>
                        <div style={s.slaBarWrap}><div style={s.slaBarFill(pct, color)} /></div>
                      </div>
                    )}
                  </td>
                  <td style={s.td}>
                    <select
                      style={{ ...s.badge(STATUS_META[l.status]?.badge || '#f5f5f5', STATUS_META[l.status]?.color || '#666'),
                        border: 'none', fontFamily: 'inherit', cursor: 'pointer', paddingRight: 18 }}
                      value={l.status || 'new'}
                      onChange={e => updateStatus(l.id, e.target.value)}>
                      {Object.entries(STATUS_META).map(([k, m]) => (
                        <option key={k} value={k}>{m.pill} {m.label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={s.td}>
                    <button style={s.actionBtn('#dcf8c6')} title="Send Message" onClick={() => setMultiChannelLead(l)}>💬</button>
                    <button style={s.actionBtn('#f0f0f0')} title="Edit" onClick={() => openEdit(l)}>✏️</button>
                    <button style={s.actionBtn('#fee2e2')} title="Delete" onClick={() => deleteLead(l.id)}>🗑</button>
                    {l.status !== 'converted' && (
                      <button style={s.actionBtn('#fce4ec')} title="Reassign now" onClick={() => reassign(l.id)}>🔁</button>
                    )}
                    <button style={s.actionBtn('#e8f5e9')} title="Quick Action" onClick={() => setQuickLead(l)}>⚡</button>
                    {l.last_agent_reply_at && (!l.last_customer_reply_at || new Date(l.last_customer_reply_at) < new Date(l.last_agent_reply_at)) && (
                      <button style={s.actionBtn('#fff3e0')} title="Send reminder" onClick={() => window.open(`https://wa.me/${(l.phone||'').replace(/[^0-9]/g,'')}?text=${encodeURIComponent('Hi ' + (l.name||'') + ', following up on your enquiry. Are you still interested?')}`, '_blank')}>⏰</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!leads.length && (
              <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', color: '#aaa', padding: 30 }}>No leads match this filter</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{editLead ? 'Edit Lead' : '+ Manual Lead'}</h2>
            {!editLead && (
              <>
                <input style={s.modalInput} placeholder="Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                <input style={s.modalInput} placeholder="Phone *" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </>
            )}
            <input style={s.modalInput} placeholder="Product tag (e.g. Cotton Saree)" value={form.product_tag || ''} onChange={e => setForm({ ...form, product_tag: e.target.value })} />
            <select style={s.modalInput} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              {STATUS_ORDER.map(k => <option key={k} value={k}>{STATUS_META[k].pill} {STATUS_META[k].label}</option>)}
            </select>
            <select style={s.modalInput} value={form.assigned_agent_id} onChange={e => setForm({ ...form, assigned_agent_id: e.target.value })}>
              <option value="">Unassigned</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            {!editLead && (
              <input style={s.modalInput} placeholder="Keyword (optional)" value={form.keyword} onChange={e => setForm({ ...form, keyword: e.target.value })} />
            )}
            <LeadCustomFieldInputs values={customVals} setValues={setCustomVals} leadType={leadType} setLeadType={setLeadType} inputStyle={s.modalInput} />
            <textarea style={{ ...s.modalInput, minHeight: 60 }} placeholder="Notes (optional)" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ ...s.btn('#f0f0f0', '#333'), flex: 1, padding: 10 }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...s.btn('#25d366', '#fff'), flex: 1, padding: 10 }} onClick={saveLead}>Save</button>
            </div>
          </div>
        </div>
      )}
      {quickLead && <LeadQuickAction lead={quickLead} onSave={quickSave} onClose={() => setQuickLead(null)} />}
      {timelineLead && <LeadTimeline lead={timelineLead} onClose={() => setTimelineLead(null)} onQuickAction={(l) => { setTimelineLead(null); setQuickLead(l); }} />}
      {showCSV && <LeadCSVImport onClose={() => setShowCSV(false)} onDone={fetchLeads} />}
      {multiChannelLead && <LeadMultiChannel lead={multiChannelLead} onClose={() => setMultiChannelLead(null)} />}
    </div>
  );
}

// =================================================================
// AGENT DASHBOARD (table-row layout)
// =================================================================
function AgentDashboard({ leads, updateStatus }) {
  const [agentFilter, setAgentFilter] = useState('all');
  const myLeads = leads.filter(l => {
    if (['converted','lost'].includes(l.status)) return false;
    if (agentFilter === 'followup') return l.last_agent_reply_at && (!l.last_customer_reply_at || new Date(l.last_customer_reply_at) < new Date(l.last_agent_reply_at));
    return true;
  });
  const slaWarn = leads.find(l => l.sla_display === 'warning' || l.sla_display === 'exceeded');
  const convertedCount = leads.filter(l => l.status === 'converted').length;
  const conversionRate = leads.length ? Math.round((convertedCount / leads.length) * 100) : 0;

  const timeAgo = (ts) => {
    if (!ts) return '—';
    const diffMs = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const quickReply = (l) => {
    const phone = (l.phone || '').replace(/[^0-9]/g, '');
    const msg = encodeURIComponent(`Hi ${l.name || ''}, following up on your enquiry. Are you still interested?`);
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  return (
    <div>
      <div style={{ ...s.header, background: 'linear-gradient(135deg,#1a237e,#283593)' }}>
        <div>
          <div style={s.headerTitle}>👋 My Leads</div>
          <div style={s.headerSub}>vbot.co.in/agent-login</div>
        </div>
      </div>

      <div style={{ ...s.statsGrid, gridTemplateColumns: 'repeat(5,1fr)' }}>
        <div style={s.statCard('#1a1a2e')}><div style={s.statNum('#1a1a2e')}>{leads.length}</div><div style={s.statLbl}>Total Assigned</div></div>
        <div style={s.statCard('#25d366')}><div style={s.statNum('#25d366')}>{leads.filter(l => l.status === 'new').length}</div><div style={s.statLbl}>New / Pending</div></div>
        <div style={s.statCard('#ff9800')}><div style={s.statNum('#ff9800')}>{leads.filter(l => l.sla_display === 'warning').length}</div><div style={s.statLbl}>SLA Warning</div></div>
        <div style={s.statCard('#2196f3')}><div style={s.statNum('#2196f3')}>{convertedCount}</div><div style={s.statLbl}>Converted</div></div>
        <div style={s.statCard('#085041')}><div style={s.statNum('#085041')}>{conversionRate}%</div><div style={s.statLbl}>Conversion Rate</div></div>
      </div>

      {slaWarn && (
        <div style={{ margin: '16px 20px', padding: '14px 18px', background: '#fff8e1', border: '1px solid #ffcc02', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#e65100' }}>SLA Warning — {slaWarn.name}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{slaWarn.hours_left} working hours remaining. Contact now!</div>
          </div>
        </div>
      )}

      <div style={{ padding: '0 20px 12px', display: 'flex', gap: 8 }}>
        <div onClick={() => setAgentFilter('all')} style={s.chip(agentFilter==='all')}>All</div>
        <div onClick={() => setAgentFilter('followup')} style={s.chip(agentFilter==='followup', '#854F0B')}>⏰ Followup</div>
      </div>
      <style>{`
        .lead-row2 { transition: background .12s; }
        .lead-row2:nth-child(even) { background: #fafbfc; }
        .lead-row2:hover { background: #eef9f3 !important; }
      `}</style>
      <div style={{ padding: '0 20px 20px', overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Customer</th>
              <th style={s.th}>Source</th>
              <th style={s.th}>Product / Ad</th>
              <th style={s.th}>Last Reply</th>
              <th style={s.th}>SLA</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Notes</th>
              <th style={s.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {myLeads.length === 0 && (
              <tr><td colSpan={8} style={{ padding: '40px 14px', textAlign: 'center', color: '#bbb', fontSize: 13 }}>
                📭 No leads assigned to you yet.
              </td></tr>
            )}
            {myLeads.map(l => {
              const src = SOURCE_META[l.source] || SOURCE_META.whatsapp;
              const color = slaColor(l.hours_left, 15);
              const pct = l.hours_left != null ? Math.max(4, 100 - (l.hours_left / 15) * 100) : 100;
              return (
                <tr key={l.id} className="lead-row2">
                  <td style={s.td}>
                    <div style={{ fontWeight: 700, color: '#1a1a2e' }}>{l.name}</div>
                    <div style={{ fontSize: 11, color: '#aaa' }}>📱 {l.phone}</div>
                  </td>
                  <td style={s.td}>
                    <span style={s.badge(src.bg, src.color)}>{l.keyword ? `💬 ${l.keyword}` : src.label}</span>
                  </td>
                  <td style={s.td}>{l.product_tag || '— Manual'}</td>
                  <td style={{ ...s.td, color: '#888' }}>{timeAgo(l.last_customer_reply_at)}</td>
                  <td style={s.td}>
                    {l.hours_left == null ? (
                      <span style={{ fontSize: 11, color: '#bbb' }}>— No SLA</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 80 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color }}>
                          {l.hours_left <= 0 ? '🔴 Exceeded' : `${l.hours_left} hrs left`}
                        </span>
                        <div style={s.slaBarWrap}><div style={s.slaBarFill(pct, color)} /></div>
                      </div>
                    )}
                  </td>
                  <td style={s.td}>
                    <select
                      style={{ ...s.badge(STATUS_META[l.status]?.badge || '#f5f5f5', STATUS_META[l.status]?.color || '#666'),
                        border: 'none', fontFamily: 'inherit', cursor: 'pointer', paddingRight: 18 }}
                      value={l.status || 'new'}
                      onChange={e => updateStatus(l.id, e.target.value)}>
                      {Object.entries(STATUS_META).map(([k, m]) => (
                        <option key={k} value={k}>{m.pill} {m.label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...s.td, textAlign: 'center', color: l.notes ? '#888' : '#ddd' }}>{l.notes ? '📝' : '—'}</td>
                  <td style={s.td}>
                    <button style={s.actionBtn('#dcf8c6')} title="WhatsApp" onClick={() => quickReply(l)}>💬</button>
                  </td>
                </tr>
              );
            })}
            {!myLeads.length && (
              <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', color: '#aaa', padding: 30 }}>No open leads assigned to you 🎉</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =================================================================
// WA FORMS CONFIG
// =================================================================
function WaFormsConfig() {
  const [config, setConfig] = useState({
    trigger_keywords: 'hi, hello, interested, hey',
    welcome_message: 'Hi {1}! Welcome to Next Automation 👋\nHow would you like to place your order?',
    buttons: [
      { name: 'Get Quote', type: 'lead_create', product_tag: 'Cotton Saree' },
      { name: 'Website Order', type: 'open_url', url: 'https://nextautomation.in' },
    ],
    sla_hours: 30, working_hours_per_day: 9, auto_reassign: true, reassignment_order: 'round_robin',
  });
  const [agentOrder, setAgentOrder] = useState([]);
  const [agents, setAgents] = useState([]);
  const [toast, setToast] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/wa-forms?business_id=' + bizId);
        if (r.data) setConfig(r.data);
      } catch (e) {}
      try {
        const ag = await api.get('/agents?business_id=' + bizId);
        const agentList = ag.data?.agents || [];
        setAgents(agentList);
        setAgentOrder(agentList.map(a => a.id));
      } catch (e) {}
    })();
  }, []);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2200); };

  const updateButton = (i, field, val) => {
    const buttons = [...config.buttons];
    buttons[i] = { ...buttons[i], [field]: val };
    setConfig({ ...config, buttons });
  };

  const addButton = () => {
    setConfig({ ...config, buttons: [...config.buttons, { name: `New Option ${config.buttons.length + 1}`, type: 'lead_create', product_tag: '' }] });
  };

  const removeButton = (i) => {
    if (config.buttons.length <= 1) return;
    setConfig({ ...config, buttons: config.buttons.filter((_, idx) => idx !== i) });
  };

  const saveButtons = async () => {
    try {
      await api.post('/wa-forms', {
        business_id: bizId,
        trigger_keywords: config.trigger_keywords,
        welcome_message: config.welcome_message,
        buttons: config.buttons,
      });
      flash('💾 Lead Buttons Saved!');
    } catch (e) { flash('❌ Save failed'); }
  };

  const saveSla = async () => {
    try {
      await api.post('/wa-forms/sla', {
        business_id: bizId, sla_hours: config.sla_hours, working_hours_per_day: config.working_hours_per_day,
        auto_reassign: config.auto_reassign, reassignment_order: config.reassignment_order, agent_order: agentOrder,
      });
      flash('💾 SLA Config Saved!');
    } catch (e) { flash('❌ Save failed'); }
  };

  const isListMode = config.buttons.length > 3;

  return (
    <div style={{ padding: 24, position: 'relative' }}>
      <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>⚙️ WA Forms — Lead Button Config</h2>
      <p style={{ fontSize: 12, color: '#aaa', marginBottom: 20 }}>Configure keyword triggers for automatic buttons · Set up SLA assignment rules</p>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eee', padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>🔘 Lead Capture Buttons</h3>

        <label style={{ fontSize: 12, fontWeight: 600 }}>Trigger Keywords</label>
        <input style={{ width: '100%', padding: 10, border: '1.5px solid #eee', borderRadius: 8, margin: '6px 0 14px', fontSize: 13 }}
          value={config.trigger_keywords} onChange={e => setConfig({ ...config, trigger_keywords: e.target.value })} />

        <label style={{ fontSize: 12, fontWeight: 600 }}>Welcome Message</label>
        <textarea style={{ width: '100%', padding: 10, border: '1.5px solid #eee', borderRadius: 8, margin: '6px 0 14px', minHeight: 60, fontSize: 13 }}
          value={config.welcome_message} onChange={e => setConfig({ ...config, welcome_message: e.target.value })} />

        <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, marginBottom: 12,
          background: isListMode ? '#fff3e0' : '#e3f2fd', color: isListMode ? '#e65100' : '#1565c0' }}>
          {isListMode
            ? `📋 List Message mode — ${config.buttons.length} options (max 10). Customer must tap "View Options" to choose.`
            : `📲 Reply Buttons mode — ${config.buttons.length} button${config.buttons.length > 1 ? 's' : ''}`}
        </div>

        {config.buttons.map((b, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, padding: 10, background: '#f9f9f9', borderRadius: 8, border: '1px solid #eee' }}>
            <input style={{ flex: 1, padding: 8, border: '1.5px solid #ddd', borderRadius: 6, fontSize: 12 }}
              value={b.name} onChange={e => updateButton(i, 'name', e.target.value)} />
            <select style={{ padding: 8, border: '1.5px solid #ddd', borderRadius: 6, fontSize: 12 }}
              value={b.type} onChange={e => updateButton(i, 'type', e.target.value)}>
              <option value="lead_create">Lead Create</option>
              <option value="open_url">Open URL</option>
              <option value="send_message">Send Message</option>
            </select>
            {b.type === 'open_url' && (
              <input style={{ flex: 1, padding: 8, border: '1.5px solid #ddd', borderRadius: 6, fontSize: 12 }}
                placeholder="https://example.com" value={b.url || ''} onChange={e => updateButton(i, 'url', e.target.value)} />
            )}
            {b.type === 'lead_create' && (
              <input style={{ width: 160, padding: 8, border: '1.5px solid #ddd', borderRadius: 6, fontSize: 12 }}
                placeholder="Product tag" value={b.product_tag || ''} onChange={e => updateButton(i, 'product_tag', e.target.value)} />
            )}
            <button style={{ background: 'none', border: 'none', color: '#f44336', fontSize: 16, cursor: 'pointer' }} onClick={() => removeButton(i)}>✕</button>
          </div>
        ))}

        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <button style={s.btn('#fff', '#25d366')} onClick={addButton}>+ Add Button</button>
          <button style={s.btn('#25d366', '#fff')} onClick={saveButtons}>💾 Save Lead Buttons</button>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eee', padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>⏱ SLA & Auto Reassignment Config</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>SLA Working Hours</label>
            <input type="number" style={{ width: '100%', padding: 10, border: '1.5px solid #eee', borderRadius: 8, marginTop: 6, fontSize: 13 }}
              value={config.sla_hours} onChange={e => setConfig({ ...config, sla_hours: +e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Working Hours / Day</label>
            <input type="number" style={{ width: '100%', padding: 10, border: '1.5px solid #eee', borderRadius: 8, marginTop: 6, fontSize: 13 }}
              value={config.working_hours_per_day} onChange={e => setConfig({ ...config, working_hours_per_day: +e.target.value })} />
          </div>
        </div>

        <label style={{ fontSize: 12, fontWeight: 600 }}>Agent Assignment Order (round robin)</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {agentOrder.map((id, idx) => {
            const agent = agents.find(a => a.id === id);
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#f9f9f9', borderRadius: 8, border: '1px solid #eee' }}>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{idx + 1}. {agent?.name || `Agent #${id}`}</span>
                <button style={s.btn('#f0f0f0', '#333')} disabled={idx === 0}
                  onClick={() => { const o = [...agentOrder]; [o[idx-1], o[idx]] = [o[idx], o[idx-1]]; setAgentOrder(o); }}>↑</button>
                <button style={s.btn('#f0f0f0', '#333')} disabled={idx === agentOrder.length - 1}
                  onClick={() => { const o = [...agentOrder]; [o[idx+1], o[idx]] = [o[idx], o[idx+1]]; setAgentOrder(o); }}>↓</button>
              </div>
            );
          })}
        </div>

        <button style={{ ...s.btn('#25d366', '#fff'), marginTop: 16 }} onClick={saveSla}>💾 Save SLA Config</button>
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#1a1a2e', color: '#fff', padding: '12px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
